import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Users, Leaf, DollarSign } from "lucide-react";
import FeaturedRolls from "./landing/FeaturedRolls";

export default function Landing() {
  return (
    <div>
      {/* HERO — split so the storefront photo (with the SANDAR SUSHI sign) is never cropped */}
      <section className="relative overflow-hidden isolate bg-neutral-950 text-white">
        {/* Backdrop tint (subtle) */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="order-2 lg:order-1">
            <p className="eyebrow text-white/80 mb-5 fade-up">Freshness in Every Bite.</p>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight fade-up" data-testid="hero-title">
              Freshness in<br/><span className="text-[hsl(var(--primary))]">Every Bite.</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-xl leading-relaxed fade-up">
              Authentic Japanese cuisine, chef-rolled daily with high-quality ingredients — right in your neighborhood.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 fade-up">
              <Link to="/menu">
                <Button size="lg" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 h-12 px-8 text-base" data-testid="hero-view-menu">
                  View Our Menu <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
              </Link>
              <Link to="/story">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 text-base bg-white/10 backdrop-blur border-white/40 text-white hover:bg-white hover:text-neutral-900" data-testid="hero-our-story">
                  Our Story
                </Button>
              </Link>
            </div>
          </div>

          {/* Storefront photo — never cropped, full logo + name always visible */}
          <div className="order-1 lg:order-2 relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-[hsl(var(--primary))]/20 blur-3xl -z-10" />
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/br94bm0r_805BE49E-37C9-4405-A7CF-925052BE2200.PNG"
              alt="Sandar Sushi storefront"
              className="w-full h-auto rounded-3xl shadow-2xl ring-1 ring-white/10"
              data-testid="hero-storefront"
            />
          </div>
        </div>
      </section>

      {/* VALUE PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {icon: Leaf, title: "Fresh Ingredients", sub: "Quality you can taste."},
            {icon: Sparkles, title: "Skilled Chefs", sub: "Authentic Japanese taste."},
            {icon: Users, title: "Our Community", sub: "Proudly serving our neighbors."},
            {icon: DollarSign, title: "Great Value", sub: "Delicious food, everyday."},
          ].map(({icon:Icon, title, sub}) => (
            <div key={title} className="p-6 rounded-2xl bg-white border border-neutral-200/70 card-hover">
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[hsl(var(--primary))]"/>
              </div>
              <h3 className="font-display font-semibold text-lg">{title}</h3>
              <p className="text-sm text-neutral-500 mt-1">{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-2 items-center">
        <div>
          <p className="eyebrow mb-4">Our Story</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mb-6">A Passion for<br/>Fresh Sushi.</h2>
          <p className="text-neutral-600 leading-relaxed max-w-lg">
            Sandar Sushi was founded with a simple belief — great food brings people together. We're proud to serve freshly prepared sushi and Japanese favorites, made with care and quality ingredients.
          </p>
          <Link to="/story"><Button variant="link" className="px-0 mt-4 text-[hsl(var(--primary))]" data-testid="story-learn-more">Learn More <ArrowRight className="ml-1 w-4 h-4"/></Button></Link>
        </div>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1512132411229-c30391241dd8?auto=format&fit=crop&w=1600&q=80" alt="Chef preparing sushi" className="rounded-3xl w-full h-[480px] object-cover"/>
          <div className="absolute -bottom-6 -left-6 bg-[hsl(var(--accent))] text-neutral-900 rounded-2xl px-5 py-4 shadow-xl">
            <div className="font-display font-bold text-2xl">Simple.</div>
            <div className="font-display font-bold text-2xl">Fresh.</div>
            <div className="font-display font-bold text-2xl text-[hsl(var(--primary))]">Delicious.</div>
          </div>
        </div>
      </section>

      {/* ADMIN-SELECTED HOMEPAGE FAVORITES */}
      <FeaturedRolls />

      {/* LOCATIONS CTA */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="eyebrow mb-4">Visit Us</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Great Food, Right in Your Neighborhood.</h2>
            <p className="mt-4 text-neutral-400 max-w-lg leading-relaxed">
              Find us inside the places you already go — supermarkets, airports, campuses, and workplaces.
            </p>
            <Link to="/locations">
              <Button size="lg" className="mt-8 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="find-location-btn">Find a Location</Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="text-4xl font-display font-bold text-[hsl(var(--accent))]">8</div>
              <p className="text-sm text-neutral-400 mt-1">Locations & growing</p>
            </div>
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="text-xl font-display font-bold">Mon – Sun</div>
              <p className="text-sm text-neutral-400 mt-1">9:00 AM – 6:00 PM ET</p>
            </div>
            <div className="col-span-2 p-6 rounded-2xl bg-[hsl(var(--primary))]">
              <p className="text-sm text-white/80">Nearest featured</p>
              <div className="text-2xl font-display font-bold">Fort Belvoir</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
